import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import "./notification.css";
import { ArrowLeft, CheckCheck } from "lucide-react";
import { createPortal } from "react-dom";
/* ── relative time ── */
const formatRelativeTime = (date) => {
  const diff = Math.floor((Date.now() - new Date(date)) / 1000);
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const notifDay = new Date(date);
  notifDay.setHours(0, 0, 0, 0);
  if (notifDay.getTime() === today.getTime() - 86400000) return "Yesterday";
  return new Date(date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/* ── icon per type (✅ les vrais types du modèle NotificationUser) ── */
const typeIcon = (type) => {
  const map = {
    PAYMENT_ACCEPTED: "✅",
    PAYMENT_REJECTED: "❌",
    PAYMENT_APPROVED_FOR_USER: "💳",
    PROFILE_VERIFIED: "✅",
    PROFILE_REJECTED: "🚫",
    WITHDRAW_ACCEPTED: "✅",
    WITHDRAW_REJECTED: "❌",
    RECHARGE_ACCEPTED: "🎉",
    RECHARGE_REJECTED: "❌",
    SUBSCRIPTION_ACTIVATED: "✅",
    SUBSCRIPTION_REJECTED: "🚫",
    DEFAULT: "🔔",
  };
  return map[type] || map.DEFAULT;
};

/* Enlève le markdown (**gras**) que le backend met dans certains messages */
const cleanMessage = (msg = "") => msg.replace(/\*\*/g, "");

const NotificationsUser = ({ onClose, onUnreadChange }) => {
  const [notifications, setNotifications] = useState([]);
  const [markingAll, setMarkingAll] = useState(false);
  // ✅ compteur calculé depuis la liste affichée (une seule source de vérité)
  const unreadCount = notifications.filter((n) => !n.read).length;
  const API_URL = process.env.REACT_APP_API_URL;
  const getToken = () => localStorage.getItem("token");
  const [bdRect, setBdRect] = useState(null);

  /* ── fetch ── */
  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/notificationsUser`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setNotifications(
        [...data].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
      );
    } catch (e) {
      console.error(e);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchNotifications();
    const iv = setInterval(fetchNotifications, 30000);
    return () => clearInterval(iv);
  }, [fetchNotifications]);

  // ✅ informe la sidebar du vrai nombre de non-lues
  useEffect(() => {
    onUnreadChange?.(unreadCount);
  }, [unreadCount, onUnreadChange]);

  /* ── toggle single ── */
  const toggleRead = async (id, current) => {
    if (current) return;
    try {
      await axios.put(
        `${API_URL}/notificationsUser/${id}/read`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, read: true } : n)),
      );
    } catch (e) {
      console.error(e);
    }
  };

  /* ── mark all read ── */
  const markAllRead = async () => {
    if (markingAll || unreadCount === 0) return;
    setMarkingAll(true);
    try {
      const unread = notifications.filter((n) => !n.read);
      await Promise.all(
        unread.map((n) =>
          axios.put(
            `${API_URL}/notificationsUser/${n._id}/read`,
            {},
            { headers: { Authorization: `Bearer ${getToken()}` } },
          ),
        ),
      );
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (e) {
      console.error(e);
    }
    setMarkingAll(false);
  };

  return createPortal(
    <>
      <div
        className="fv-backdrop"
        style={bdRect ? { inset: "auto", ...bdRect } : undefined}
      />
      <div className="bonus-popup">
        {/* ── Header ── */}
        <div className="notif-header">
          <div>
            <button
              className="notif-header__back"
              onClick={onClose}
              type="button"
              aria-label="Back"
            >
              <ArrowLeft size={15} />
            </button>
          </div>

          <p className="notif-header__title">Notifications</p>

          <div className="notif-header__right">
            {unreadCount > 0 && (
              <span className="notif-header__badge">{unreadCount}</span>
            )}
            {unreadCount > 0 && (
              <button
                className="notif-header__back"
                type="button"
                onClick={markAllRead}
                disabled={markingAll}
                title="Mark all as read"
                aria-label="Mark all as read"
              >
                <CheckCheck size={14} />
              </button>
            )}
          </div>
        </div>
        {/* ── List ── */}
        <div className="notifications-container-user">
          {notifications.length > 0 ? (
            <ul className="notification-list">
              {notifications.map((notif) => (
                <li key={notif._id} className="notification-item">
                  <div
                    className={`notification-content ${notif.read ? "read" : "unread"}`}
                    onClick={() => toggleRead(notif._id, notif.read)}
                    role={notif.read ? "article" : "button"}
                    tabIndex={notif.read ? -1 : 0}
                    onKeyDown={(e) =>
                      !notif.read &&
                      e.key === "Enter" &&
                      toggleRead(notif._id, notif.read)
                    }
                  >
                    {/* Icon */}
                    <div className="notif-icon-wrap">
                      {notif.type ? (
                        <span style={{ fontSize: 18 }}>
                          {typeIcon(notif.type)}
                        </span>
                      ) : (
                        <img
                          src="images/sidebar/notifications.PNG"
                          alt=""
                          className="notification-icon-user"
                        />
                      )}
                    </div>

                    {/* Text */}
                    <div className="notification-text">
                      <p className="notification-message">
                        {cleanMessage(notif.message)}
                      </p>
                      <span className="notification-info">
                        {formatRelativeTime(notif.createdAt)}
                      </span>
                    </div>

                    {/* Status */}
                    <span className="notification-status">
                      {notif.read ? "Read" : "New"}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="no-notifications">No notifications</p>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
};

export default NotificationsUser;
